import React, { useState, useEffect, useMemo } from 'react';
import { Archive } from 'lucide-react';
import Card from '../../components/Card';
import { TableColGroup, TABLE_HEADER_CELL_CLASS } from '../../components/Table';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/apiService';
import { renderCell, isEmptyCell } from '../../utils/helpers';
import {
  buildCabinetMatrix,
  cabinetStatusClass,
} from '../../utils/cabinetPlacementsGrid';

const CabinetPlacementsView = () => {
  const { user } = useAuth();
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const { error: showError } = useToast();

  useEffect(() => {
    if (!user?.id) return;
    loadPlacements();
  }, [user?.id]);

  const loadPlacements = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCabinetPlacements(user?.id);
      setPlacements(Array.isArray(data) ? data : []);
    } catch (error) {
      showError('Erreur lors du chargement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const matrix = useMemo(() => buildCabinetMatrix(placements), [placements]);
  const { accessories, locations, getAt, totalQuantity } = matrix;

  const renderQty = (data) =>
    data == null ? renderCell(null) : isEmptyCell(data.quantity) ? renderCell(data.quantity) : data.quantity;

  const renderStatus = (data) => {
    if (!data?.status) return renderCell(null);
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cabinetStatusClass(data.status)}`}>
        {data.status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Archive className="w-8 h-8 text-red-600" />
          Emplacement des Armoires
        </h1>
        <p className="text-gray-600 mt-1">
          Consultation des emplacements des accessoires dans les armoires (données saisies par l&apos;administrateur)
        </p>
      </div>

      {placements.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          <Card>
            <div className="text-center py-1">
              <p className="text-2xl sm:text-3xl font-bold text-blue-600">{accessories.length}</p>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Types d&apos;accessoires</p>
            </div>
          </Card>
          <Card>
            <div className="text-center py-1">
              <p className="text-2xl sm:text-3xl font-bold text-green-600">{locations.length}</p>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Emplacements</p>
            </div>
          </Card>
          <Card>
            <div className="text-center py-1">
              <p className="text-2xl sm:text-3xl font-bold text-red-600">{totalQuantity}</p>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Total éléments</p>
            </div>
          </Card>
        </div>
      )}

      <Card>
        {placements.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Archive className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune donnée disponible</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              Aucun emplacement d&apos;armoire n&apos;est enregistré pour votre compte. Contactez l&apos;administrateur
              pour configurer vos armoires (menu admin « Emplacement Armoires »).
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-500 mb-3 px-1">
              Faites défiler horizontalement si le tableau dépasse l&apos;écran.
            </p>
            <div className="overflow-x-auto pfe-table-scroll -mx-1">
              <table
                className="pfe-data-table table-fixed w-full divide-y divide-gray-200 text-xs sm:text-sm"
                style={{ minWidth: `${Math.max(480, 120 + locations.length * 140)}px` }}
              >
                <TableColGroup
                  weights={[1.6, ...locations.flatMap(() => [0.65, 0.65])]}
                  headers={['Accessoires', ...locations.flatMap(() => ['Nombre', 'État'])]}
                />
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      rowSpan={2}
                      className="sticky left-0 z-10 bg-gray-50 px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 min-w-[120px]"
                    >
                      Accessoires
                    </th>
                    {locations.map((loc, idx) => (
                      <th
                        key={loc}
                        colSpan={2}
                        className={`px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider ${TABLE_HEADER_CELL_CLASS} ${idx < locations.length - 1 ? 'border-r border-gray-200' : ''}`}
                      >
                        {loc}
                      </th>
                    ))}
                  </tr>
                  <tr className="bg-gray-100">
                    {locations.map((loc, idx) => (
                      <React.Fragment key={`sub-${loc}`}>
                        <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 w-14">Nombre</th>
                        <th
                          className={`px-2 py-2 text-center text-xs font-semibold text-gray-700 w-20 ${idx < locations.length - 1 ? 'border-r border-gray-200' : ''}`}
                        >
                          État
                        </th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {accessories.map((accessoire) => (
                    <tr key={accessoire} className="hover:bg-gray-50 transition-colors">
                      <td className="sticky left-0 z-[1] bg-white px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                        {accessoire}
                      </td>
                      {locations.map((loc, idx) => {
                        const cell = getAt(accessoire, loc);
                        return (
                          <React.Fragment key={`${accessoire}-${loc}`}>
                            <td className="px-2 py-3 text-sm text-center text-gray-700">{renderQty(cell)}</td>
                            <td
                              className={`px-2 py-3 text-sm text-center text-gray-600 ${idx < locations.length - 1 ? 'border-r border-gray-200' : ''}`}
                            >
                              {renderStatus(cell)}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default CabinetPlacementsView;
